import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Store, Coffee, ShoppingBag, 
  Bike, Package, MapPin, 
  ArrowRight, UserCheck, Zap
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } }
};

const Services = () => {
  const primaryServices = [
    {
      icon: <ShoppingBag size={24} />,
      title: "Online Shop Delivery",
      description: "High-volume parcel movement for e-commerce brands. We handle the final mile so you can focus on scaling your sales.",
      tags: ["Bulk Parcels", "Daily Pickups", "City-wide"]
    },
    {
      icon: <Store size={24} />,
      title: "Restaurant Solutions",
      description: "Fast-response fleet support for food service. We ensure meals reach customers at peak quality and speed.",
      tags: ["Hot Food", "Scheduled Shifts", "Rapid Dispatch"]
    },
    {
      icon: <Coffee size={24} />,
      title: "Cafe & Boutique",
      description: "Careful transport for delicate items, pastries, and boutique goods within Budapest's central districts.",
      tags: ["Delicate Handling", "Morning Routes", "Fixed Pricing"]
    }
  ];

  const operationalHighlights = [
    {
      title: "55+ Professional Fleet",
      description: "Our workforce is trained specifically for the streets of Budapest, navigating traffic with elite agility.",
      icon: <Bike size={18} />
    },
    {
      title: "Central Hub Deployment",
      description: "Based in District XIV, we deploy across all 23 districts with unmatched city-wide response times.",
      icon: <MapPin size={18} />
    },
    {
      title: "Partner Consistency",
      description: "We work as a seamless extension of your brand, providing the same reliability as an internal team.",
      icon: <UserCheck size={18} />
    }
  ];

  return (
    <main className="min-h-screen bg-white text-[#032B53] selection:bg-[#0099FF] selection:text-white">
      
      {/* --- Hero Section --- */}
      <section className="bg-[#032B53] pt-32 pb-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-[1px] w-10 bg-[#0099FF]"></span>
              <span className="text-[#0099FF] font-bold tracking-[0.3em] text-[10px] uppercase">Budapest Infrastructure</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-8 leading-[0.9]">
              Physical <br />
              <span className="text-white/30 italic">Fleet Services.</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100/60 font-light leading-relaxed max-w-xl">
              Specialized delivery infrastructure for Hungarian businesses. We provide the fleet; you provide the product.
            </p>
          </motion.div>
        </div>
        {/* Decorative background element */}
        <Package className="absolute right-[-10%] bottom-[-10%] text-white opacity-5 w-[500px] h-[500px] -rotate-12 pointer-events-none" />
      </section>

      {/* --- Core Industry Services --- */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            className="grid lg:grid-cols-3 gap-8"
          >
            {primaryServices.map((service, index) => (
              <motion.div 
                key={index} 
                variants={fadeInUp}
                className="p-12 rounded-[3.5rem] bg-gray-50/50 border border-gray-100 hover:border-[#0099FF] transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="bg-white w-16 h-16 rounded-2xl shadow-sm flex items-center justify-center mb-10 group-hover:bg-[#0099FF] group-hover:text-white transition-all duration-500">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl font-black mb-6 tracking-tighter">{service.title}</h3>
                  <p className="text-gray-500 font-medium mb-10 text-lg leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {service.tags.map((tag, tIndex) => (
                    <span key={tIndex} className="text-[9px] font-bold uppercase tracking-widest bg-white border border-gray-200 px-4 py-1.5 rounded-full text-gray-400">
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- Logistics Diagram/Flow --- */}
      <section className="py-24 bg-gray-50 rounded-[4rem] mx-4">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4">Partnership Lifecycle</h2>
            <div className="h-1 w-12 bg-[#0099FF] mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16 text-left">
            {[
              { num: "01", title: "Pickup", desc: "Daily or on-demand collection from your hub, restaurant, or boutique." },
              { num: "02", title: "Transit", desc: "Bypassing Budapest gridlock via our agile 55+ bike messenger fleet." },
              { num: "03", title: "Arrival", desc: "Safe delivery to your customer, verified through our partner networks." }
            ].map((step, i) => (
              <motion.div 
                key={i} 
                {...fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="space-y-6"
              >
                <span className="text-6xl font-black text-[#032B53]/5 block">{step.num}</span>
                <h4 className="text-2xl font-black tracking-tight">{step.title}</h4>
                <p className="text-gray-500 font-medium leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Operational Standards --- */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-20">
            {operationalHighlights.map((item, index) => (
              <motion.div 
                key={index} 
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="space-y-6"
              >
                <div className="w-12 h-12 bg-[#0099FF]/10 rounded-xl flex items-center justify-center text-[#0099FF]">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-black tracking-tighter">{item.title}</h3>
                <p className="text-gray-500 font-medium leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA Section --- */}
      <section className="pb-24 px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto bg-[#032B53] rounded-[4rem] py-24 px-6 text-center relative overflow-hidden"
        >
          <Zap size={200} className="absolute -right-20 -bottom-20 text-white/5 rotate-12" />
          <div className="relative z-10 text-white max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-8xl font-black tracking-tighter mb-12 leading-[0.9]">
              Scale your <br />
              <span className="text-[#0099FF]">capacity.</span>
            </h2>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-6 bg-white text-[#032B53] px-14 py-6 rounded-3xl text-xl font-black hover:bg-[#0099FF] hover:text-white transition-all active:scale-95 shadow-2xl"
            >
              Start Partnership
              <ArrowRight size={24} />
            </Link>
            <div className="mt-16 flex flex-wrap justify-center gap-8 opacity-30 text-[10px] font-black uppercase tracking-[0.4em]">
              <span>Wolt Partner</span>
              <span>Express Fork Partner</span>
              <span>Budapest XIV Hub</span>
            </div>
          </div>
        </motion.div>
      </section>
      
      {/* Bottom SEO Tagline */}
      <footer className="py-12 text-center opacity-20">
        <p className="text-[10px] font-bold uppercase tracking-[0.5em]">Professional Courier Fleet Hungary</p>
      </footer>
    </main>
  );
};

export default Services;